import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  UpdateUserPasswordDocument,
  type UpdateUserPasswordMutation,
  type UpdateUserPasswordMutationVariables,
} from "@src/__generated__/graphql";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { appStore } from "@src/stores/AppStore";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { action, computed, makeObservable } from "mobx";
import type { UserModel } from "../../models";
import { FormChangeUserPassword } from "./form";

export class ChangeUserPasswordStore {
  /**
   * Form for changing user password
   */
  changePasswordForm: FormChangeUserPassword | null = null;

  private mutator = new MutationHelper<
    UpdateUserPasswordMutation,
    UpdateUserPasswordMutationVariables
  >(UpdateUserPasswordDocument);

  disclosure = new DisclosureState<{ user: UserModel }>({
    onOpen: (additionalData) => {
      if (!additionalData?.user) {
        captureEvent({
          message:
            "FE: Trying to open change password modal without user data.",
        });
        appStore.UIStore.toast({
          status: "error",
        });
        return;
      }

      this.changePasswordForm = new FormChangeUserPassword(
        additionalData?.user.id,
      );
    },
    onClose: () => {
      this.changePasswordForm = null;
    },
  });

  constructor() {
    makeObservable(this);
  }

  @computed
  get isChangePasswordLoading() {
    return this.mutator.isLoading.value;
  }

  @action.bound
  async updateUserPassword() {
    if (!this.changePasswordForm) {
      appStore.UIStore.toast({
        status: "error",
      });
      return;
    }

    const [data, error] = await this.mutator.mutate({
      input: this.changePasswordForm.serialize(),
    });

    if (error) return;

    if (data?.updateUserPassword.id) {
      appStore.UIStore.toast({
        title: t`User password was changed`,
        status: "success",
      });
      this.disclosure.onClose();
    }
  }
}
